/**
 * Industry-Specific Data Sources Configuration
 * =============================================
 * Maps industries to their relevant data sources, compliance bodies,
 * and integration partners. Enables context-aware display of trust indicators.
 */

// Australian Government & Regulatory Bodies
export const GOVERNMENT_SOURCES = {
  // Core Federal Sources (Cross-Industry)
  ato: {
    id: 'ato',
    name: 'ATO',
    fullName: 'Australian Taxation Office',
    description: 'Tax compliance, BAS, payroll data',
    logo: '/logos/ato.svg',
    color: '#1E3A5F',
    trustLevel: 'Official',
    url: 'https://www.ato.gov.au',
    dataTypes: ['tax_benchmarks', 'bas_compliance', 'payroll_tax', 'super_guarantee']
  },
  abs: {
    id: 'abs',
    name: 'ABS',
    fullName: 'Australian Bureau of Statistics',
    description: 'Economic indicators, industry statistics',
    logo: '/logos/abs.svg',
    color: '#00529B',
    trustLevel: 'Official',
    url: 'https://www.abs.gov.au',
    dataTypes: ['industry_benchmarks', 'economic_indicators', 'labour_stats', 'population_data']
  },
  rba: {
    id: 'rba',
    name: 'RBA',
    fullName: 'Reserve Bank of Australia',
    description: 'Interest rates, economic outlook',
    logo: '/logos/rba.svg',
    color: '#004B87',
    trustLevel: 'Official',
    url: 'https://www.rba.gov.au',
    dataTypes: ['interest_rates', 'cash_rate', 'economic_forecasts', 'inflation_data']
  },
  asic: {
    id: 'asic',
    name: 'ASIC',
    fullName: 'Australian Securities & Investments Commission',
    description: 'Corporate compliance, financial services',
    logo: '/logos/asic.svg',
    color: '#003366',
    trustLevel: 'Official',
    url: 'https://www.asic.gov.au',
    dataTypes: ['company_registry', 'financial_compliance', 'afs_licence', 'director_duties']
  },
  apra: {
    id: 'apra',
    name: 'APRA',
    fullName: 'Australian Prudential Regulation Authority',
    description: 'Banking, insurance, superannuation regulation',
    logo: '/logos/apra.svg',
    color: '#1A1A5E',
    trustLevel: 'Official',
    url: 'https://www.apra.gov.au',
    dataTypes: ['prudential_standards', 'capital_requirements', 'risk_management']
  },
  
  // Healthcare & Allied Health
  ahpra: {
    id: 'ahpra',
    name: 'AHPRA',
    fullName: 'Australian Health Practitioner Regulation Agency',
    description: 'Health practitioner registration, compliance',
    logo: '/logos/ahpra.svg',
    color: '#00A6A0',
    trustLevel: 'Official',
    url: 'https://www.ahpra.gov.au',
    dataTypes: ['practitioner_registration', 'compliance_status', 'cpd_requirements']
  },
  tga: {
    id: 'tga',
    name: 'TGA',
    fullName: 'Therapeutic Goods Administration',
    description: 'Medical devices, pharmaceuticals regulation',
    logo: '/logos/tga.svg',
    color: '#005A8C',
    trustLevel: 'Official',
    url: 'https://www.tga.gov.au',
    dataTypes: ['product_compliance', 'medical_devices', 'pharmaceutical_standards']
  },
  medicare: {
    id: 'medicare',
    name: 'Medicare',
    fullName: 'Medicare Australia',
    description: 'Medicare billing, PBS data',
    logo: '/logos/medicare.svg',
    color: '#008542',
    trustLevel: 'Official',
    url: 'https://www.servicesaustralia.gov.au/medicare',
    dataTypes: ['billing_data', 'pbs_claims', 'bulk_billing_rates']
  },
  
  // Food & Hospitality
  fsanz: {
    id: 'fsanz',
    name: 'FSANZ',
    fullName: 'Food Standards Australia New Zealand',
    description: 'Food safety standards, labelling requirements',
    logo: '/logos/fsanz.svg',
    color: '#5C8A2F',
    trustLevel: 'Official',
    url: 'https://www.foodstandards.gov.au',
    dataTypes: ['food_safety_standards', 'labelling_compliance', 'allergen_requirements']
  },
  liquor_gaming: {
    id: 'liquor_gaming',
    name: 'L&G',
    fullName: 'Liquor & Gaming Authority',
    description: 'Liquor licensing, gaming compliance',
    logo: '/logos/liquor-gaming.svg',
    color: '#7B1FA2',
    trustLevel: 'Official',
    url: 'https://www.liquorandgaming.nsw.gov.au',
    dataTypes: ['liquor_licence', 'gaming_compliance', 'rsa_rsrg']
  },
  
  // Property & Construction
  fair_trading: {
    id: 'fair_trading',
    name: 'Fair Trading',
    fullName: 'NSW Fair Trading / Consumer Affairs',
    description: 'Consumer protection, licensing',
    logo: '/logos/fair-trading.svg',
    color: '#0055B8',
    trustLevel: 'Official',
    url: 'https://www.fairtrading.nsw.gov.au',
    dataTypes: ['licence_status', 'consumer_compliance', 'trust_accounts']
  },
  abcb: {
    id: 'abcb',
    name: 'ABCB',
    fullName: 'Australian Building Codes Board',
    description: 'National Construction Code compliance',
    logo: '/logos/abcb.svg',
    color: '#D35400',
    trustLevel: 'Official',
    url: 'https://www.abcb.gov.au',
    dataTypes: ['ncc_compliance', 'building_standards', 'energy_efficiency']
  },
  safework: {
    id: 'safework',
    name: 'SafeWork',
    fullName: 'SafeWork Australia',
    description: 'WHS compliance, safety standards',
    logo: '/logos/safework.svg',
    color: '#E74C3C',
    trustLevel: 'Official',
    url: 'https://www.safeworkaustralia.gov.au',
    dataTypes: ['whs_compliance', 'safety_statistics', 'incident_rates']
  },
  
  // Education
  teqsa: {
    id: 'teqsa',
    name: 'TEQSA',
    fullName: 'Tertiary Education Quality and Standards Agency',
    description: 'Higher education regulation',
    logo: '/logos/teqsa.svg',
    color: '#2E4053',
    trustLevel: 'Official',
    url: 'https://www.teqsa.gov.au',
    dataTypes: ['provider_registration', 'quality_standards', 'course_accreditation']
  },
  asqa: {
    id: 'asqa',
    name: 'ASQA',
    fullName: 'Australian Skills Quality Authority',
    description: 'VET sector regulation, RTO compliance',
    logo: '/logos/asqa.svg',
    color: '#1ABC9C',
    trustLevel: 'Official',
    url: 'https://www.asqa.gov.au',
    dataTypes: ['rto_registration', 'training_compliance', 'qualification_standards']
  },
  
  // Transport & Logistics
  nhvr: {
    id: 'nhvr',
    name: 'NHVR',
    fullName: 'National Heavy Vehicle Regulator',
    description: 'Heavy vehicle compliance, chain of responsibility',
    logo: '/logos/nhvr.svg',
    color: '#F39C12',
    trustLevel: 'Official',
    url: 'https://www.nhvr.gov.au',
    dataTypes: ['vehicle_compliance', 'cor_obligations', 'fatigue_management']
  },
  
  // Agriculture
  daff: {
    id: 'daff',
    name: 'DAFF',
    fullName: 'Department of Agriculture, Fisheries and Forestry',
    description: 'Agricultural compliance, biosecurity',
    logo: '/logos/daff.svg',
    color: '#27AE60',
    trustLevel: 'Official',
    url: 'https://www.agriculture.gov.au',
    dataTypes: ['export_compliance', 'biosecurity', 'commodity_data']
  },
  
  // Environmental
  epa: {
    id: 'epa',
    name: 'EPA',
    fullName: 'Environment Protection Authority',
    description: 'Environmental compliance, waste management',
    logo: '/logos/epa.svg',
    color: '#16A085',
    trustLevel: 'Official',
    url: 'https://www.epa.nsw.gov.au',
    dataTypes: ['environmental_compliance', 'waste_reporting', 'emissions_data']
  },
  cer: {
    id: 'cer',
    name: 'CER',
    fullName: 'Clean Energy Regulator',
    description: 'Emissions reporting, renewable energy',
    logo: '/logos/cer.svg',
    color: '#2ECC71',
    trustLevel: 'Official',
    url: 'https://www.cleanenergyregulator.gov.au',
    dataTypes: ['emissions_reporting', 'rec_certificates', 'nger_compliance']
  }
};

// Industry-Specific Data Source Mapping
export const INDUSTRY_DATA_SOURCES = {
  hospitality: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['fsanz', 'liquor_gaming', 'safework'],
    integrations: ['xero', 'myob', 'square', 'tanda', 'deputy', 'kounta'],
    benchmarkSources: ['ato', 'abs', 'restaurant_association'],
    description: 'Revenue from POS, compliance from Food Safety & Liquor authorities'
  },
  retail: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['fair_trading', 'accc'],
    integrations: ['xero', 'myob', 'shopify', 'square', 'vend', 'cin7'],
    benchmarkSources: ['ato', 'abs', 'retail_association'],
    description: 'Sales data from POS, consumer compliance from Fair Trading'
  },
  healthcare: {
    primary: ['ato', 'abs', 'medicare'],
    compliance: ['ahpra', 'tga'],
    integrations: ['xero', 'myob', 'cliniko', 'mindbody', 'halaxy'],
    benchmarkSources: ['ato', 'abs', 'medicare', 'ahpra'],
    description: 'Medicare billing data, AHPRA registration compliance'
  },
  professional: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['asic', 'fair_trading'],
    integrations: ['xero', 'myob', 'quickbooks', 'hubspot', 'salesforce'],
    benchmarkSources: ['ato', 'abs', 'professional_associations'],
    description: 'Time billing from practice software, ASIC company compliance'
  },
  fitness: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['fair_trading', 'safework'],
    integrations: ['xero', 'myob', 'mindbody', 'glofox', 'square'],
    benchmarkSources: ['ato', 'abs', 'fitness_australia'],
    description: 'Member data from gym software, WHS compliance'
  },
  property: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['fair_trading', 'asic'],
    integrations: ['xero', 'myob', 'hubspot', 'salesforce', 'domain'],
    benchmarkSources: ['ato', 'abs', 'rba', 'corelogic'],
    description: 'Sales data, trust account compliance from Fair Trading'
  },
  construction: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['abcb', 'safework', 'fair_trading'],
    integrations: ['xero', 'myob', 'quickbooks', 'procore', 'buildertrend'],
    benchmarkSources: ['ato', 'abs', 'hia', 'mba'],
    description: 'Project costing, NCC compliance, WHS safety data'
  },
  automotive: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['fair_trading', 'accc'],
    integrations: ['xero', 'myob', 'dealersocket', 'square'],
    benchmarkSources: ['ato', 'abs', 'fcai'],
    description: 'Sales & service data, consumer guarantee compliance'
  },
  education: {
    primary: ['ato', 'abs'],
    compliance: ['teqsa', 'asqa'],
    integrations: ['xero', 'myob', 'hubspot', 'canvas', 'moodle'],
    benchmarkSources: ['ato', 'abs', 'teqsa', 'asqa'],
    description: 'Enrollment data, quality assurance compliance'
  },
  beauty: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['ahpra', 'tga', 'fair_trading'],
    integrations: ['xero', 'myob', 'square', 'fresha', 'timely'],
    benchmarkSources: ['ato', 'abs', 'beauty_association'],
    description: 'Booking revenue, cosmetic procedure compliance'
  },
  manufacturing: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['safework', 'epa', 'cer'],
    integrations: ['xero', 'myob', 'quickbooks', 'dear', 'cin7'],
    benchmarkSources: ['ato', 'abs', 'aigroup'],
    description: 'Production data, WHS & environmental compliance'
  },
  entertainment: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['liquor_gaming', 'fair_trading', 'safework'],
    integrations: ['xero', 'myob', 'square', 'eventbrite', 'ticketmaster'],
    benchmarkSources: ['ato', 'abs', 'live_performance'],
    description: 'Ticketing revenue, licensing & crowd safety compliance'
  },
  technology: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['asic', 'accc'],
    integrations: ['xero', 'myob', 'stripe', 'hubspot', 'salesforce'],
    benchmarkSources: ['ato', 'abs', 'acs', 'austrade'],
    description: 'SaaS revenue data, consumer compliance'
  },
  finance: {
    primary: ['ato', 'abs', 'rba', 'asic', 'apra'],
    compliance: ['asic', 'apra', 'austrac'],
    integrations: ['xero', 'myob', 'hubspot', 'salesforce', 'iress'],
    benchmarkSources: ['ato', 'abs', 'rba', 'apra', 'asic'],
    description: 'AUM data, AFSL compliance, prudential standards'
  },
  accounting: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['asic', 'tpb', 'caanz'],
    integrations: ['xero', 'myob', 'quickbooks', 'hubspot'],
    benchmarkSources: ['ato', 'abs', 'caanz', 'cpaaustralia'],
    description: 'Client billing, tax agent registration compliance'
  },
  agriculture: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['daff', 'epa', 'nrm'],
    integrations: ['xero', 'myob', 'agworld', 'agriwebb'],
    benchmarkSources: ['ato', 'abs', 'abares', 'daff'],
    description: 'Farm production data, biosecurity compliance'
  },
  logistics: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['nhvr', 'safework'],
    integrations: ['xero', 'myob', 'cartoncloud', 'detrack'],
    benchmarkSources: ['ato', 'abs', 'ata'],
    description: 'Delivery data, chain of responsibility compliance'
  },
  trades: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['fair_trading', 'safework'],
    integrations: ['xero', 'myob', 'servicem8', 'jobber', 'simpro'],
    benchmarkSources: ['ato', 'abs', 'trades_associations'],
    description: 'Job costing, licensing & WHS compliance'
  },
  legal: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['asic', 'law_society', 'olsc'],
    integrations: ['xero', 'myob', 'clio', 'leap', 'actionstep'],
    benchmarkSources: ['ato', 'abs', 'law_society'],
    description: 'Time billing, trust account & practising certificate compliance'
  },
  accommodation: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['liquor_gaming', 'fair_trading', 'fsanz'],
    integrations: ['xero', 'myob', 'cloudbeds', 'roomraccoon', 'little_hotelier'],
    benchmarkSources: ['ato', 'abs', 'str_global', 'taa'],
    description: 'Booking revenue, liquor & food safety compliance'
  },
  gaming: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['liquor_gaming', 'accc'],
    integrations: ['xero', 'myob', 'stripe', 'square'],
    benchmarkSources: ['ato', 'abs', 'igea'],
    description: 'Sales data, gaming classification compliance'
  },
  environmental: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['epa', 'cer', 'daff'],
    integrations: ['xero', 'myob', 'quickbooks'],
    benchmarkSources: ['ato', 'abs', 'cer', 'epa'],
    description: 'Revenue data, environmental & emissions compliance'
  },
  wholesale: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['accc', 'fair_trading'],
    integrations: ['xero', 'myob', 'cin7', 'dear', 'tradegecko'],
    benchmarkSources: ['ato', 'abs', 'wholesale_associations'],
    description: 'Distribution data, consumer compliance'
  },
  pharmacy: {
    primary: ['ato', 'abs', 'medicare'],
    compliance: ['ahpra', 'tga', 'pbs'],
    integrations: ['xero', 'myob', 'fred', 'minfos'],
    benchmarkSources: ['ato', 'abs', 'medicare', 'pga'],
    description: 'PBS dispensing data, pharmacist registration compliance'
  },
  veterinary: {
    primary: ['ato', 'abs', 'rba'],
    compliance: ['ava', 'avbc', 'tga'],
    integrations: ['xero', 'myob', 'ezyvet', 'vetsource'],
    benchmarkSources: ['ato', 'abs', 'ava'],
    description: 'Clinic revenue, veterinary registration compliance'
  }
};

// Cross-Industry Compliance Bodies (applies to all)
export const UNIVERSAL_COMPLIANCE = ['ato', 'abs', 'safework', 'fair_trading'];

// Get all relevant data sources for an industry
export const getIndustryDataSources = (industry) => {
  const config = INDUSTRY_DATA_SOURCES[industry] || INDUSTRY_DATA_SOURCES.professional;
  
  // Combine all sources
  const allSourceIds = [
    ...new Set([
      ...config.primary,
      ...config.compliance,
      ...(config.benchmarkSources || [])
    ])
  ];
  
  // Map to full source objects
  const sources = allSourceIds.map(id => GOVERNMENT_SOURCES[id]).filter(Boolean);
  
  return {
    ...config,
    sources,
    governmentSources: config.primary.map(id => GOVERNMENT_SOURCES[id]).filter(Boolean),
    complianceSources: config.compliance.map(id => GOVERNMENT_SOURCES[id]).filter(Boolean),
    allSourceIds
  };
};

// Get compliance sources specific to an industry
export const getComplianceSources = (industry) => {
  const config = INDUSTRY_DATA_SOURCES[industry] || INDUSTRY_DATA_SOURCES.professional;
  return config.compliance.map(id => GOVERNMENT_SOURCES[id]).filter(Boolean);
};

// Get benchmark data sources for an industry
export const getBenchmarkSources = (industry) => {
  const config = INDUSTRY_DATA_SOURCES[industry] || INDUSTRY_DATA_SOURCES.professional;
  const benchmarkIds = config.benchmarkSources || config.primary;
  return benchmarkIds.map(id => GOVERNMENT_SOURCES[id]).filter(Boolean);
};

// Check if a source is relevant for an industry
export const isSourceRelevant = (sourceId, industry) => {
  const config = INDUSTRY_DATA_SOURCES[industry];
  if (!config) return UNIVERSAL_COMPLIANCE.includes(sourceId);
  
  return [
    ...config.primary,
    ...config.compliance,
    ...(config.benchmarkSources || [])
  ].includes(sourceId);
};

// Get recommended integrations for an industry
export const getRecommendedIntegrations = (industry) => {
  const config = INDUSTRY_DATA_SOURCES[industry] || INDUSTRY_DATA_SOURCES.professional;
  return config.integrations || ['xero', 'myob', 'quickbooks'];
};

export default {
  GOVERNMENT_SOURCES,
  INDUSTRY_DATA_SOURCES,
  UNIVERSAL_COMPLIANCE,
  getIndustryDataSources,
  getComplianceSources,
  getBenchmarkSources,
  isSourceRelevant,
  getRecommendedIntegrations
};
